// Input for QR code scanning griptonite tags
import { CheckCircle, PhotoCamera } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
} from "@mui/joy";
import { ClimbType } from "griptonite-api-client";
import { HTTPError } from "griptonite-api-client/client";
import QrScanner from "qr-scanner";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useFormContext, useController } from "react-hook-form";

import { useTagQuery } from "../../hooks/tags";
import TagWarningIcon from "../TagWarningIcon";

class InvalidTagError extends Error {
  constructor() {
    super("Invalid tag URL");
  }
}

const getTagFromUrl = (url: string) => {
  const regex = /https:\/\/(go\.)?griptonite.io\/n\/(\w+)/;
  const match = url.match(regex);
  if (!match) {
    throw new InvalidTagError();
  }
  return match[2];
};

// focusMode and zoom are part of the Image Capture spec and missing from
// TypeScript's DOM types. Only some browsers (Chrome on Android) report them.
type CameraCapabilities = MediaTrackCapabilities & {
  focusMode?: string[];
  zoom?: { min: number; max: number };
};

// Zoom applied when the camera can't autofocus, so people hold the phone
// further from the tag, beyond the distance where a fixed lens blurs.
const FALLBACK_ZOOM = 4;

// The camera chosen on the first scan, reused for the rest of the page load.
let chosenCameraId: string | undefined;

const getCapabilities = (track?: MediaStreamTrack) =>
  track?.getCapabilities?.() as CameraCapabilities | undefined;

const getActiveCamera = (video: HTMLVideoElement | null) => {
  const stream = video?.srcObject;
  const track = stream instanceof MediaStream ? stream.getVideoTracks()[0] : undefined;
  return { track, capabilities: getCapabilities(track) };
};

const hasContinuousFocus = (capabilities?: CameraCapabilities) =>
  Boolean(capabilities?.focusMode?.includes("continuous"));

// Browsers that don't report focus modes are left alone, since they may well
// autofocus anyway (Safari on iOS, for example).
const lacksContinuousFocus = (capabilities?: CameraCapabilities) =>
  Boolean(capabilities?.focusMode) && !hasContinuousFocus(capabilities);

const getRearCameras = async () => {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((d) => d.kind === "videoinput" && /back|rear|environment/i.test(d.label));
};

// Phones with several rear lenses can hand us a fixed-focus one (the
// ultra-wide on a Galaxy S22, for example). Opening each camera in turn is the
// only way to read its focus modes. Android can't open a second camera while
// one is streaming, so the scanner must be paused first.
const findContinuousFocusCamera = async () => {
  for (const device of await getRearCameras()) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: device.deviceId } },
      });
      const capabilities = getCapabilities(stream.getVideoTracks()[0]);
      stream.getTracks().forEach((t) => t.stop());
      if (hasContinuousFocus(capabilities)) {
        return device.deviceId;
      }
    } catch (err) {
      console.debug(`Couldn't open camera: ${device.label}`, err);
    }
  }
  return undefined;
};

// Gets the running scanner onto a camera that can focus on a tag. If the
// browser picked a rear camera without continuous autofocus, switch to one
// that has it. If none does, zoom in instead. The chosen camera is remembered
// for the rest of the page load and trusted without searching again.
const configureCamera = async (
  scanner: QrScanner,
  video: HTMLVideoElement | null,
  searchForCamera: boolean,
  isCancelled: () => boolean,
) => {
  try {
    let { track, capabilities } = getActiveCamera(video);

    if (searchForCamera && lacksContinuousFocus(capabilities)) {
      await scanner.pause(true);
      const deviceId = await findContinuousFocusCamera();
      if (isCancelled()) {
        return;
      }
      if (deviceId) {
        await scanner.setCamera(deviceId);
      }
      await scanner.start();
      ({ track, capabilities } = getActiveCamera(video));
    }

    if (!track || !capabilities) {
      return;
    }
    if (capabilities.deviceId) {
      chosenCameraId = capabilities.deviceId;
    }
    if (hasContinuousFocus(capabilities)) {
      await track.applyConstraints({
        advanced: [{ focusMode: "continuous" } as MediaTrackConstraintSet],
      });
    } else if (capabilities.zoom) {
      const { min, max } = capabilities.zoom;
      await track.applyConstraints({
        advanced: [
          {
            zoom: Math.min(Math.max(FALLBACK_ZOOM, min), max),
          } as MediaTrackConstraintSet,
        ],
      });
    }
  } catch (err) {
    // Focus and zoom are niceties, so never let them break scanning.
    console.debug("Couldn't configure camera", err);
  }
};

const Scanner = ({
  onSuccess,
  onError,
}: {
  onSuccess: (tagId: string) => void;
  onError?: (err: Error) => void;
}) => {
  const videoEl = useRef<HTMLVideoElement>(null);
  const callbacksRef = useRef({ onSuccess, onError });

  const [isScanning, setIsScanning] = useState(false);

  useLayoutEffect(() => {
    callbacksRef.current = { onSuccess, onError };
  }, [onSuccess, onError]);

  useEffect(() => {
    if (!videoEl.current) {
      return;
    }
    const rememberedCameraId = chosenCameraId;
    const scanner = new QrScanner(
      videoEl.current,
      // @ts-expect-error
      ({ data }: { data: string }) => {
        try {
          const tagId = getTagFromUrl(data);
          callbacksRef.current.onSuccess(tagId);
        } catch (err) {
          callbacksRef.current.onError?.(err as Error);
        }
      },
      {
        returnDetailedScanResult: true,
        onDecodeError: callbacksRef.current.onError,
        maxScansPerSecond: 5,
        preferredCamera: rememberedCameraId ?? "environment",
      },
    );
    scanner.setInversionMode("both");
    let cancelled = false;
    scanner
      .start()
      .then(() => {
        setIsScanning(true);
        configureCamera(scanner, videoEl.current, !rememberedCameraId, () => cancelled);
      })
      .catch((err) => callbacksRef.current.onError?.(err));

    return () => {
      cancelled = true;
      scanner.destroy();
    };
  }, []);

  return (
    <Box sx={{ maxWidth: "80vw", position: "relative" }}>
      <video ref={videoEl} style={{ maxWidth: "100%", display: "block" }} />
      {isScanning ? (
        <>
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              padding: 1,
              color: "white",
              textAlign: "center",
              fontSize: "sm",
            }}
          >
            Position the tag’s QR code in the centre of the frame
          </Box>
        </>
      ) : (
        <CircularProgress
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />
      )}
    </Box>
  );
};

export const ScannerModal = ({
  open,
  onClose,
  onSuccess,
  onError,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (tagId: string) => void;
  onError?: (err: Error) => void;
}) => {
  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ padding: 0, overflow: "hidden" }}>
        <ModalClose
          variant="solid"
          sx={{
            borderRadius: "100%",
            backgroundColor: "#000A",
            "&:hover": {
              backgroundColor: "#000",
            },
          }}
        />
        <Scanner onSuccess={onSuccess} onError={onError} />
      </ModalDialog>
    </Modal>
  );
};

export const TagScanButton = ({
  value,
  onChange,
  onError,
}: {
  value?: string | null;
  onChange: (value: string | null) => void;
  onError: (err: Error) => void;
}) => {
  const [isScanning, setIsScanning] = useState(false);

  return (
    <>
      <ScannerModal
        open={isScanning}
        onSuccess={(tagId: string) => {
          onChange(tagId);
          setIsScanning(false);
        }}
        onClose={() => setIsScanning(false)}
        onError={onError}
      />
      {value ? (
        <>
          <Button
            startDecorator={<CheckCircle sx={{ color: "#45D27E" }} />}
            onClick={() => {
              setIsScanning(true);
            }}
            variant="soft"
            color="neutral"
          >
            Tag scanned
          </Button>

          <Box
            sx={{
              justifyContent: "center",
              display: "flex",
              flexDirection: "row",
            }}
          >
            <Button variant="plain" color="danger" onClick={() => onChange(null)} size="sm">
              Clear
            </Button>
          </Box>
        </>
      ) : (
        <Button
          startDecorator={<PhotoCamera />}
          onClick={(_e) => {
            setIsScanning(true);
          }}
        >
          Scan QR code
        </Button>
      )}
    </>
  );
};

export const TagScanInput = ({
  name = "tag",
  prefix = "",
  autoSetRouteFields = true,
}: {
  name?: string;
  prefix?: string;
  autoSetRouteFields?: boolean;
}) => {
  const applyPrefix = (p: string | undefined, s: string) => (p ? `${p}.${s}` : s);
  const { control, clearErrors, setError, setValue, watch } = useFormContext();
  name = applyPrefix(prefix, name);
  const { field, fieldState } = useController({
    name,
    control,
  });
  const startTime: string | null = watch(applyPrefix(prefix, "startTime"));
  const climbType: ClimbType | null = watch(applyPrefix(prefix, "climbType"));

  const { data: tag, error: tagFetchError } = useTagQuery(
    fieldState.isDirty ? field.value : undefined,
  );

  useEffect(() => {
    if (!tagFetchError) {
      clearErrors(name);
    }
    if (tagFetchError instanceof HTTPError && tagFetchError.response.status === 404) {
      setError(name, new Error("Tag not found"));
    } else {
      setError(name, new Error("Couldn't validate the tag, please try again"));
    }
  }, [tagFetchError, clearErrors, setError, name]);

  useEffect(() => {
    if (tag && autoSetRouteFields) {
      if (tag.number) {
        setValue(applyPrefix(prefix, "circuit.number"), tag.number);
      }
      if (tag.color) {
        setValue(applyPrefix(prefix, "colors"), tag.color);
      }
    }
  }, [tag, setValue, autoSetRouteFields]);

  const showTagWarning = startTime && !field.value && climbType !== "SPORT";

  return (
    <FormControl error={Boolean(fieldState.error)}>
      <FormLabel>
        Tag {showTagWarning ? <TagWarningIcon sx={{ marginLeft: 0.25 }} /> : null}
      </FormLabel>
      <TagScanButton
        value={field.value}
        onChange={field.onChange}
        onError={(error) =>
          control.setError(
            name,
            error instanceof InvalidTagError
              ? error
              : new Error("There was a problem scanning the tag, please try again."),
          )
        }
      />
      <FormHelperText>{fieldState.error?.message}</FormHelperText>
    </FormControl>
  );
};
